import React from "react";
import {observer} from "mobx-react";
import {action, ObservableMap} from "mobx";
import PageAncestor from "../../common/page/pageAncestor";
import {MODULE, PageProps, PERMISSION} from "../../metamodel/metaConstant";
import ConfirmationDialog from "../../components/confirmationDialog/confirmationDialog";
import mdEntityService from "../../metamodel/mdEntityService";
import {DomainAncestor} from "../../common/domain/DomainAncestor";
import {ValidationMessage} from "../../common/domain/commonDomain";
import uiService from "../../common/service/uiService";
import Toolmenu from "../../components/toolmenu/toolmenu";
import {ZzFlatButton} from "../../components/buttons/buttons";
import tenantService from "../service/tenantService";
import {ParamTable} from "../domain/tenantDomain";
import {PARAM_TABLE_ENTITY, TENANT_ENTITY, TENANT_GROUP_ENTITY} from "../constants";
import {FormProps, IDomainObject, preSave} from "../../form/formUtils";
import {ErrorComp} from "../../components/errorComp/errorComp";
import ParamTableForm, {ParamTableFormDefinition} from "./paramTableForm";

@observer
export default class ParamKeyDetail extends PageAncestor<PageProps, undefined> {

  static module = MODULE.core;
  static title = 'Param key Detail';
  static icon = 'key';
  static protectedPage = true;

  static fetchData(routerParams, query) {
    console.log("ParamKeyDetail#fetchData(%O)", query);

    let metadataPromise = mdEntityService.fetchEntityMetadata([PARAM_TABLE_ENTITY]);
    let entityGenGroupObjectPromise: Promise<ParamTable>;

    if (routerParams.id === 'new') {
      entityGenGroupObjectPromise = Promise.resolve(new ParamTable({$new: true}));
    } else if (routerParams.id === 'copy') {
      entityGenGroupObjectPromise = tenantService.readParamTableRow(query.copyId)
        .then((paramTableObject: ParamTable) => {
          paramTableObject.cleanOnCopy();
          paramTableObject.tenantId = undefined;

          return paramTableObject;
        })
    } else {
      entityGenGroupObjectPromise = tenantService.readParamTableRow(routerParams.id);
    }

    return Promise.all([metadataPromise, entityGenGroupObjectPromise])
      .then(([entityMap, paramTableObject]: [any, ParamTable]) => {
        tenantService.paramTableObject = paramTableObject.initAfterRead(routerParams.id === 'new');

        tenantService.paramTableObject.loadTenantOptions(ParamTableFormDefinition.formName);
        tenantService.paramTableObject.resolveTenantOrTenantGroup(ParamTableFormDefinition.formName);
      });
  }

  private confirmationDeleteDialogRef: ConfirmationDialog;
  confirmationDeleteDialogRefHandler = comp => this.confirmationDeleteDialogRef = comp;

  componentWillUnmount() {
    tenantService.paramTableObject = null;
  }

  findRootObject(): DomainAncestor {
    return tenantService.paramTableObject;
  }

  customValidate = (paramTableObject: ParamTable) => {
    let errors: ValidationMessage[] = [];

    if (paramTableObject.tenant && paramTableObject.tenantGroup)
      errors.push({message: "Choose one of tenant or tenant group"})
    else if(!paramTableObject.tenant && !paramTableObject.tenantGroup)
      errors.push({message: "Please choose tenant or tenant group"})

    return errors;
  };

  @action onSave = (evt) => {
    console.log('onSave paramTableObjectId = %s, $new = %s', tenantService.paramTableObject.getFlattenId(), tenantService.paramTableObject.$new);
    uiService.showToolbarActionFlag();

    let result = preSave(tenantService.paramTableObject, this.customValidate, true);
    if (!result) {
      return;
    }

    tenantService.saveParamTable(tenantService.paramTableObject.createEditTenantDto(), tenantService.paramTableObject.$new)
      .then(
        action("paramTableSave", (paramTableJson: IDomainObject) => {
            const wasNew = tenantService.paramTableObject.cleanAfterSave(paramTableJson);

            if (wasNew)
              this.context.router.replaceWith('paramKeyDetail', {id: tenantService.paramTableObject.getFlattenId()});

            uiService.hideToolbarActionFlag();
          }
        ),
        (error) => {
          tenantService.paramTableObject.$errors = error.getMessages();
        }
      );

  };

  @action onAdd = (evt) => {
    console.log('onAdd');
    this.context.router.transitionTo("paramKeyDetail", {id: 'new'});
  };

  @action onCopy = (evt) => {
    console.log('onCopy');
    this.context.router.transitionTo("paramKeyDetail", {id: 'copy'}, {copyId: tenantService.paramTableObject.getFlattenId()});
  };

  @action onDelete = (evt) => {
    console.log('onDelete');

    new Promise((resolve, reject) => {
      this.confirmationDeleteDialogRef.open(resolve, reject, 'Do you really want to delete this param table row?');
    }).then(() => {

      uiService.showToolbarActionFlag();

      tenantService.deleteTableParam(tenantService.paramTableObject.getFlattenId())
        .then(_ => {
          this.goBackOrHome();

          uiService.hideToolbarActionFlag();
        }, error => {
          tenantService.paramTableObject.$errors = error.getMessages();
        });

    }).catch((e) => {
    })
  };

  private _createToolMenu(paramTableObject: ParamTable, gridLocation: string) {

    return (
      <Toolmenu chainGridLocation={gridLocation} backAction={this.onBack} backDisabled={History.length <= 1}>

        <ZzFlatButton onClick={this.onSave} disabled={!paramTableObject.$dirty} label="Save"
                      permission={paramTableObject.$new ? PERMISSION.New : PERMISSION.Update}/>
        <ZzFlatButton onClick={this.onAdd} label="New"
                      permission={PERMISSION.New}/>
        <ZzFlatButton onClick={this.onCopy} disabled={paramTableObject.$new} label="Copy"
                      permission={PERMISSION.New}/>
        <ZzFlatButton onClick={this.onDelete} disabled={paramTableObject.$new} label="Delete"
                      permission={PERMISSION.Delete}/>
      </Toolmenu>
    );
  }


  render() {
    let {params, query} = this.props;

    let paramTableObject: ParamTable = tenantService.paramTableObject;

    const propsForForm: FormProps<ParamTable, ParamTable> = {
      dataObject: paramTableObject,
      rootObject: paramTableObject,
      entity: mdEntityService.getEntity(PARAM_TABLE_ENTITY),
      entities: mdEntityService.entityMap
    };

    const _renderParamTableForm: JSX.Element = (
      <ParamTableForm {...propsForForm} />
    );


    return (
      <main className="main-content" style={{display: 'flex', flexDirection: 'column'}}>
        {this._createToolMenu(paramTableObject, query.gridLocation)}
        <ErrorComp messages={paramTableObject.$errors}/>
        <ConfirmationDialog ref={this.dirtyDialogRefHandler}/>
        <ConfirmationDialog okButtonText="Yes" cancelButtonText="No"
                            ref={this.confirmationDeleteDialogRefHandler}/>
        {_renderParamTableForm}
      </main>
    );
  }
}



// WEBPACK FOOTER //
// ./src/core/tenant/paramkey/paramKeyDetail.tsx